import { CSRF_HEADER, apiErrorSchema } from "@devengo/shared";

/** An API failure carrying the Spanish message and the request id to quote to support. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly requestId: string | undefined,
    readonly details: unknown,
  ) {
    super(message);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: "same-origin",
    headers: {
      ...(body instanceof FormData ? {} : { "content-type": "application/json" }),
      ...(method === "GET" ? {} : { [CSRF_HEADER]: "1" }),
      ...extraHeaders,
    },
  };
  if (body !== undefined) init.body = body instanceof FormData ? body : JSON.stringify(body);
  const response = await fetch(path, init);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const json: unknown = text ? JSON.parse(text) : undefined;
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(json);
    if (parsed.success) {
      const { message, code, requestId, details } = parsed.data.error;
      throw new ApiError(message, response.status, code, requestId, details);
    }
    throw new ApiError(
      "No pudimos completar la operación. Probá de nuevo en un momento.",
      response.status,
      "UNKNOWN",
      response.headers.get("x-request-id") ?? undefined,
      json,
    );
  }
  return json as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  /** A create that must not run twice: the key is generated when the form opens (Article VI.5). */
  postOnce: <T>(path: string, body: unknown, idempotencyKey: string) =>
    request<T>("POST", path, body, { "Idempotency-Key": idempotencyKey }),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body ?? {}),
  upload: <T>(path: string, form: FormData) => request<T>("POST", path, form),
  uploadPut: <T>(path: string, form: FormData) => request<T>("PUT", path, form),
};

/** The errors list a voucher validation returns in `details.errors`, if any. */
export function detailErrors(error: unknown): string[] {
  if (!(error instanceof ApiError)) return [];
  const details = error.details as { errors?: unknown; issues?: { message: string }[] } | undefined;
  if (Array.isArray(details?.errors)) return details.errors.map(String);
  if (Array.isArray(details?.issues)) return details.issues.map((i) => i.message);
  return [];
}
